/**
 * PLT-7096 — the supplier-import review card's data: the review as it stands, read from the
 * card-state routes (`upserve/modules/chat/supplier-import-card-state.ts`), and the user's changes
 * to it. Every change answers with the whole review, which replaces the cached one, so the card
 * never computes a group or a count of its own.
 *
 * PLT-7097: and creating the ticked ready rows, which then move through "Being created" on their own.
 *
 * PLT-7100: and following the created suppliers to assessed, and trying again what went wrong.
 */
import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  SupplierImportCreateRequest,
  SupplierImportRetryRequest,
  SupplierImportReviewPayload,
  SupplierImportReviewSuccessPayload,
  SupplierImportReviewUpdate,
  SupplierImportRowChange,
  SupplierImportSelection,
} from '@upright/common/mcp-payloads/supplier-import-review';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';

/** While website lookups run, rows move out of "Looking up a website" on their own. */
const LOOKUP_POLL_MS = 5_000;

/** A few rows are created every few seconds, so the card follows them more closely than the lookups. */
const CREATION_POLL_MS = 3_000;

/**
 * Rows can wait for a lookup the agent has yet to start: it may show the card first and start the
 * lookup next. The card keeps asking for this long after it is drawn, so it sees the lookup begin;
 * past that, the rows take a website the user types.
 */
const AWAITING_LOOKUP_WINDOW_MS = 2 * 60 * 1000;

/** PLT-7100: an assessment takes minutes, so the card follows the created suppliers as the Suppliers list does. */
const ASSESSMENT_POLL_MS = 10_000;

/**
 * A job orphaned by a dead worker never finishes, so the card stops asking once no created supplier
 * has moved for this long, as the Suppliers list gives up on one (`IN_FLIGHT_POLL_CEILING_MS`). A
 * batch keeps moving while any of it does; a reload asks again.
 */
const ASSESSMENT_STALL_MS = 20 * 60 * 1000;

/** The created suppliers' statuses when one last changed: the card's, kept between polls. */
export interface AssessmentMovement {
  statuses: string;
  at: number;
}

/** Whether a created supplier has moved within `ASSESSMENT_STALL_MS`. Updates `movement`. */
function assessmentMoving(review: SupplierImportReviewSuccessPayload, movement: AssessmentMovement, now: number) {
  const statuses = review.rows.map(row => row.assessmentStatus ?? '').join(',');
  if (statuses !== movement.statuses) {
    movement.statuses = statuses;
    movement.at = now;
  }
  return now - movement.at <= ASSESSMENT_STALL_MS;
}

function importPath(importId: string): string {
  return `/chat/supplier-imports/${encodeURIComponent(importId)}`;
}

function reviewQueryKey(importId: string) {
  return ['chat', 'supplier-import-review', importId] as const;
}

function changeMutationKey(importId: string) {
  return ['chat', 'supplier-import-review', importId, 'change'] as const;
}

/**
 * `shownAt`: when the card was drawn, the start of the window it waits for a lookup to begin in.
 * `movement`: the card's own, mutated, so a stalled assessment is timed across polls.
 */
export function getSupplierImportReviewQuery(importId: string, shownAt: number, movement: AssessmentMovement) {
  return queryOptions({
    queryKey: reviewQueryKey(importId),
    queryFn: async () => await fetchOrThrow<SupplierImportReviewPayload>(`${importPath(importId)}/review`),
    refetchInterval: query => {
      const review = query.state.data;
      if (!review || 'dataAvailable' in review) return false;
      const now = Date.now();
      // Read on every poll, so the clock sees each change, whichever wait asks next.
      const moving = assessmentMoving(review, movement, now);
      if (review.creation.queued > 0) return CREATION_POLL_MS;
      if (review.websiteLookupRunning) return LOOKUP_POLL_MS;
      if (review.assessment.inProgress > 0 && moving) return ASSESSMENT_POLL_MS;
      const awaiting = review.counts['awaiting-website'] > 0;
      return awaiting && now - shownAt < AWAITING_LOOKUP_WINDOW_MS ? LOOKUP_POLL_MS : false;
    },
  });
}

/**
 * The review a change answered with, unless another change is still on its way: the server saves
 * changes one at a time, but their answers can come back in either order, and the last one to be
 * answered is the one that has seen them all.
 */
function applyReview(queryClient: QueryClient, importId: string, review: SupplierImportReviewSuccessPayload) {
  if (queryClient.isMutating({ mutationKey: changeMutationKey(importId) }) > 1) return;
  // A poll that left before the change was saved would otherwise land on top of its answer.
  void queryClient.cancelQueries({ queryKey: reviewQueryKey(importId) });
  queryClient.setQueryData(reviewQueryKey(importId), review);
}

function useReviewChangeMutation<Variables>(
  importId: string,
  send: (variables: Variables) => Promise<SupplierImportReviewUpdate>,
) {
  const queryClient = useQueryClient();
  return useMutation<SupplierImportReviewUpdate, Error, Variables>({
    mutationKey: changeMutationKey(importId),
    mutationFn: send,
    onSuccess: update => {
      applyReview(queryClient, importId, update.review);
    },
    onError: () => {
      if (queryClient.isMutating({ mutationKey: changeMutationKey(importId) }) > 1) return;
      void queryClient.invalidateQueries({ queryKey: reviewQueryKey(importId) });
    },
  });
}

export function useChangeSupplierImportRowMutation(importId: string) {
  return useReviewChangeMutation<{ rowNumber: number; change: SupplierImportRowChange }>(
    importId,
    async ({ rowNumber, change }) =>
      await fetchOrThrow<SupplierImportReviewUpdate>(`${importPath(importId)}/rows/${rowNumber}`, 'PATCH', change),
  );
}

export function useSelectSupplierImportRowsMutation(importId: string) {
  return useReviewChangeMutation<SupplierImportSelection>(
    importId,
    async selection =>
      await fetchOrThrow<SupplierImportReviewUpdate>(`${importPath(importId)}/selection`, 'POST', selection),
  );
}

export function useAnswerSupplierImportCurrencyMutation(importId: string) {
  return useReviewChangeMutation<{ inBuyerCurrency: boolean }>(
    importId,
    async answer =>
      await fetchOrThrow<SupplierImportReviewUpdate>(`${importPath(importId)}/spend-currency`, 'POST', answer),
  );
}

export function useCreateSupplierImportSuppliersMutation(importId: string) {
  return useReviewChangeMutation<SupplierImportCreateRequest>(
    importId,
    async request => await fetchOrThrow<SupplierImportReviewUpdate>(`${importPath(importId)}/create`, 'POST', request),
  );
}

export function useRetrySupplierImportRowsMutation(importId: string) {
  return useReviewChangeMutation<SupplierImportRetryRequest>(
    importId,
    async request => await fetchOrThrow<SupplierImportReviewUpdate>(`${importPath(importId)}/retry`, 'POST', request),
  );
}
